import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, LayoutGrid } from 'lucide-react';
import type { AdTemplate } from '@shared/schema';
import { adStyleOf } from './ad-style';
import { useCanvasStore } from './canvas-store';

type DiscoverSection = { id: string; label: string; styles: string[] };

// styles: [] => all templates (Winning Ads). Otherwise match any of these
// styleTags. The same array is handed to the Ad Library when the section opens.
// Organize by AD STYLE (parsed from the template name) — the real per-ad
// winning-ad classification — instead of the loose `styleTags` that put
// testimonials/sale ads under "Lifestyle". Sections with no cards auto-hide, so
// a sparse style (e.g. only a few Listicles) simply shows what it has.
// ("Winning Ads" sampler removed until that bucket is properly organized.)
const DISCOVER_SECTIONS: DiscoverSection[] = [
  { id: 'before-after', label: 'Before & After', styles: ['Before & After'] },
  { id: 'social-proof', label: 'Social Proof', styles: ['Social Proof'] },
  { id: 'problem-solution', label: 'Problem · Solution', styles: ['Problem-Solution'] },
  { id: 'us-vs-them', label: 'Us vs Them', styles: ['Us vs Them'] },
  { id: 'listicle', label: 'Listicle', styles: ['Listicle'] },
  { id: 'ugc', label: 'UGC Style', styles: ['UGC Style'] },
  { id: 'benefits', label: 'Benefits', styles: ['Benefits'] },
  { id: 'features', label: 'Features', styles: ['Features'] },
  { id: 'product-launch', label: 'Product Launch', styles: ['Product Launch'] },
  { id: 'reviews', label: 'Reviews', styles: ['Reviews'] },
  { id: 'sale', label: 'Sale', styles: ['Sale'] },
];

const CARDS_PER_SECTION = 12;
const PAGE_BG = '#F8F8F7';

function matchesStyles(t: AdTemplate, styles: string[]): boolean {
  if (styles.length === 0) return true;
  return styles.includes(adStyleOf(t));
}

function DiscoverCard({ template, onOpen }: { template: AdTemplate; onOpen: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      data-testid={`discover-card-${template.id}`}
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        marginBottom: 12, borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
        position: 'relative', background: '#FFFFFF', border: '1px solid #E5E2DC',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'box-shadow 150ms', breakInside: 'avoid',
      }}
    >
      <img
        src={template.imageUrl}
        alt={template.name}
        loading="lazy"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
      {hovered && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(20,20,20,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FFFFFF', color: '#1A1A1A', fontSize: 12.5, fontWeight: 600, padding: '8px 14px', borderRadius: 8 }}>
            <LayoutGrid style={{ width: 13, height: 13 }} /> Ad Library
          </span>
        </div>
      )}
    </div>
  );
}

function DiscoverSectionRow({ section, cards, onOpen }: { section: DiscoverSection; cards: AdTemplate[]; onOpen: (styles: string[], focusId?: number) => void }) {
  if (cards.length === 0) return null;
  const open = () => onOpen(section.styles);
  return (
    <div data-testid={`discover-section-${section.id}`} style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>{section.label}</span>
        <button
          data-testid={`discover-viewmore-${section.id}`}
          onClick={open}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6B6760' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#1A1A1A')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#6B6760')}
        >
          View more <ArrowRight style={{ width: 13, height: 13 }} />
        </button>
      </div>
      <div style={{ position: 'relative', maxHeight: 640, overflow: 'hidden' }}>
        <div style={{ columnCount: 4, columnGap: 12 }}>
          {cards.map((t) => <DiscoverCard key={t.id} template={t} onOpen={() => onOpen(section.styles, t.id)} />)}
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 120, background: `linear-gradient(to bottom, rgba(248,248,247,0), ${PAGE_BG})`, pointerEvents: 'none' }} />
      </div>
    </div>
  );
}

export function DiscoverSections() {
  const { data: templates = [], isLoading } = useQuery<AdTemplate[]>({ queryKey: ['/api/ad-templates'] });
  const openAdLibrary = useCanvasStore((s) => s.openAdLibrary);

  // Stable per-load shuffle so each section shows a varied sample that does not
  // reorder on re-render (mirrors AdTemplatesTab's shuffleRank). Recomputes only
  // when the template set changes (a fresh fetch).
  const shuffled = useMemo(() => {
    const arr = [...templates];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [templates]);

  // Assign each ad to at most ONE section. Walk sections top-to-bottom; each
  // claims up to CARDS_PER_SECTION not-yet-used ads matching its styles, so an
  // ad tagged for several styles (or grabbed by "Winning Ads", which matches
  // everything) never repeats across the Discover sections.
  const sectionCards = useMemo(() => {
    const used = new Set<number>();
    const byId: Record<string, AdTemplate[]> = {};
    for (const section of DISCOVER_SECTIONS) {
      const picked: AdTemplate[] = [];
      for (const t of shuffled) {
        if (picked.length >= CARDS_PER_SECTION) break;
        if (used.has(t.id)) continue;
        if (matchesStyles(t, section.styles)) {
          picked.push(t);
          used.add(t.id);
        }
      }
      byId[section.id] = picked;
    }
    return byId;
  }, [shuffled]);

  // Below the fold, so rendering nothing until templates load causes no visible
  // flash; the centered hero above is a complete screen on its own.
  if (isLoading || templates.length === 0) return null;

  return (
    <div style={{ width: '100%', maxWidth: 1080, margin: '0 auto', padding: '8px 24px 64px' }}>
      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A' }}>Discover</span>
      </div>
      {DISCOVER_SECTIONS.map((section) => (
        <DiscoverSectionRow key={section.id} section={section} cards={sectionCards[section.id]} onOpen={openAdLibrary} />
      ))}
    </div>
  );
}
